import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Check, X } from 'lucide-react';
import { Button } from '../ui/button';
import { comparisonData } from '../../data/mock';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../ui/table';

const ComparisonPage = () => {
  return (
    <div className="min-h-screen pt-20">
      {/* Hero Section */}
      <section className="relative py-24 bg-[#242726]">
        <div className="container-custom">
          <div className="max-w-3xl">
            <span className="caption text-[#c4291c] mb-4 block">Comparatif</span>
            <h1 className="heading-1 text-white mb-6">
              {comparisonData.title}
            </h1>
            <p className="body-large text-white/70">
              {comparisonData.subtitle}
            </p>
          </div>
        </div>
      </section>

      {/* Comparison Table - Desktop */}
      <section className="section-padding bg-[#1a1c1b]">
        <div className="container-custom">
          <div className="hidden md:block overflow-hidden rounded-2xl border border-white/10">
            <Table>
              <TableHeader>
                <TableRow className="bg-[#2a2d2c] border-white/10">
                  <TableHead className="text-white font-semibold w-1/5">Critère</TableHead>
                  <TableHead className="text-white/60 w-2/5">Méthode traditionnelle</TableHead>
                  <TableHead className="text-[#c4291c] font-semibold w-2/5">Solution PREBATI</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {comparisonData.criteria.map((item, index) => (
                  <TableRow key={index} className="border-white/10 hover:bg-white/5">
                    <TableCell className="text-white font-medium">{item.aspect}</TableCell>
                    <TableCell className="text-white/60">
                      <div className="flex items-start gap-3">
                        <X className="w-5 h-5 text-white/30 mt-0.5 flex-shrink-0" />
                        <span>{item.traditional}</span>
                      </div>
                    </TableCell>
                    <TableCell className="text-white">
                      <div className="flex items-start gap-3">
                        <Check className="w-5 h-5 text-[#c4291c] mt-0.5 flex-shrink-0" />
                        <span>{item.prebati}</span>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>

          {/* Comparison Cards - Mobile */}
          <div className="md:hidden space-y-6">
            {comparisonData.criteria.map((item, index) => (
              <div key={index} className="bg-[#2a2d2c] rounded-xl overflow-hidden border border-white/10">
                <div className="px-6 py-4 bg-[#1a1c1b] border-b border-white/10">
                  <h3 className="text-white font-semibold">{item.aspect}</h3>
                </div>
                <div className="divide-y divide-white/10">
                  <div className="p-6">
                    <span className="caption text-white/40 mb-2 block">Méthode traditionnelle</span>
                    <div className="flex items-start gap-3">
                      <X className="w-5 h-5 text-white/30 mt-0.5 flex-shrink-0" />
                      <span className="text-white/60 body-small">{item.traditional}</span>
                    </div>
                  </div>
                  <div className="p-6 bg-[#c4291c]/5">
                    <span className="caption text-[#c4291c] mb-2 block">Solution PREBATI</span>
                    <div className="flex items-start gap-3">
                      <Check className="w-5 h-5 text-[#c4291c] mt-0.5 flex-shrink-0" />
                      <span className="text-white body-small">{item.prebati}</span>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Summary */}
      <section className="section-padding bg-[#242726]">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <div className="grid md:grid-cols-2 gap-8">
              <div className="bg-[#1a1c1b] rounded-2xl p-8 border border-white/10">
                <h3 className="heading-3 text-white/60 mb-6">Construction traditionnelle</h3>
                <ul className="space-y-4">
                  {[
                    'Coffrage bois consommateur de temps',
                    'Équipe nombreuse nécessaire',
                    'Qualité variable selon conditions',
                    'Délais longs et imprévisibles',
                    'Portées limitées'
                  ].map((item, index) => (
                    <li key={index} className="flex items-start gap-3">
                      <X className="w-5 h-5 text-white/30 mt-0.5 flex-shrink-0" />
                      <span className="text-white/50 body-medium">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="bg-[#c4291c]/10 rounded-2xl p-8 border border-[#c4291c]/30">
                <h3 className="heading-3 text-[#c4291c] mb-6">Solution PREBATI</h3>
                <ul className="space-y-4">
                  {[
                    'Pas de coffrage requis',
                    'Équipe réduite et formation rapide',
                    'Qualité industrielle constante',
                    'Mise en œuvre rapide',
                    'Portées jusqu\'à 10 mètres'
                  ].map((item, index) => (
                    <li key={index} className="flex items-start gap-3">
                      <Check className="w-5 h-5 text-[#c4291c] mt-0.5 flex-shrink-0" />
                      <span className="text-white body-medium">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-[#1a1c1b] border-t border-white/10">
        <div className="container-custom">
          <div className="flex flex-col md:flex-row items-center justify-between gap-8">
            <div>
              <h3 className="heading-3 text-white mb-2">
                Convaincu par la solution industrielle ?
              </h3>
              <p className="text-white/60 body-medium">
                Demandez un devis pour votre prochain projet.
              </p>
            </div>
            <a href="https://wa.me/22243111616?text=" target="_blank" rel="noopener noreferrer">
              <Button className="btn-primary">
                Demander un devis
                <ArrowRight className="w-4 h-4" />
              </Button>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ComparisonPage;
